import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button';
import { cn } from '@/lib/cn';

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

/**
 * Dialog modal yang benar secara aksesibilitas.
 *
 * Tiga hal yang sering terlewat dan semuanya ditangani di sini:
 *  1. Fokus dipindah ke dalam dialog saat terbuka, dan dikembalikan ke elemen
 *     pemicu saat ditutup - tanpa ini pengguna keyboard tersesat.
 *  2. Tab terperangkap di dalam dialog, tidak bisa menyelinap ke latar.
 *  3. Halaman di belakang tidak ikut menggulir.
 */
export const Modal = ({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  closeOnBackdrop = true,
}) => {
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    // Menunggu satu frame agar panel sudah ter-render sebelum fokus dipindah.
    const frame = requestAnimationFrame(() => {
      const target = panelRef.current?.querySelector(
        '[data-autofocus], button:not([disabled]), input:not([disabled])',
      );
      target?.focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4"
      onKeyDown={handleKeyDown}
    >
      <div
        className="fixed inset-0 bg-brand-950/45 backdrop-blur-[2px]"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        aria-describedby={description ? 'modal-description' : undefined}
        className={cn(
          'relative flex max-h-[92vh] w-full flex-col overflow-hidden bg-[var(--surface-overlay)] shadow-xl',
          // Di ponsel dialog naik dari bawah seperti lembar; di layar lebar
          // ia melayang di tengah.
          'rounded-t-2xl sm:rounded-2xl',
          'animate-in',
          SIZES[size],
        )}
      >
        {title && (
          <div className="flex items-start justify-between gap-4 border-b border-[var(--border-subtle)] px-5 py-4">
            <div className="min-w-0">
              <h2 id="modal-title" className="text-base font-bold text-[var(--text-primary)]">
                {title}
              </h2>
              {description && (
                <p id="modal-description" className="mt-0.5 text-sm text-[var(--text-muted)]">
                  {description}
                </p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={onClose}
              aria-label="Tutup dialog"
              className="-mt-1 -mr-1.5"
            >
              <X className="size-4" />
            </Button>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-[var(--border-subtle)] px-5 py-3.5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

/**
 * Dialog konfirmasi untuk tindakan yang sulit dibatalkan.
 *
 * Rancangan awal tidak punya satu pun konfirmasi - padahal ada tombol hapus
 * file dan hapus pengguna di sana.
 */
export const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Anda yakin?',
  description,
  confirmLabel = 'Ya, lanjutkan',
  cancelLabel = 'Batal',
  destructive = false,
  loading = false,
}) => (
  <Modal
    open={open}
    onClose={onClose}
    title={title}
    size="sm"
    closeOnBackdrop={!loading}
    footer={
      <>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          variant={destructive ? 'danger' : 'primary'}
          onClick={onConfirm}
          loading={loading}
          data-autofocus
        >
          {confirmLabel}
        </Button>
      </>
    }
  >
    <p className="text-sm leading-relaxed text-[var(--text-secondary)]">{description}</p>
  </Modal>
);
